import { useEffect, useState } from 'react';
import { fetchDeliveryDestinations } from './api';
import type { DeliveryDestination } from '@tsr/shared-types';

/**
 * Per-agency delivery destination picker (migration 0052).
 *
 * A simple <select> the forward actions (SITREP §4.19, assistance request §4.20,
 * dispatch recommendation §4.18) render so an operator can route a forward to a
 * specific recipient. It auto-selects the default (PEOC) destination on load, so
 * the existing one-click forward flow stays one-click — the operator only touches
 * this control to route somewhere other than PEOC.
 */

const KIND_LABEL: Record<DeliveryDestination['kind'], string> = {
  peoc: 'PEOC',
  ministry: 'Ministry',
  municipality: 'Municipality',
  agency: 'Agency',
  federal: 'Federal',
};

interface Props {
  /** Currently selected destination id (undefined until the default loads). */
  value: string | undefined;
  onChange: (destinationId: string | undefined) => void;
  disabled?: boolean;
  /** testid for the <select>. */
  testid?: string;
  /** Leading label. Defaults to 'Forward to' (the forward actions); the inbound
   *  route step passes 'Route to' (Ruling 3 / Phase 3a) — same component, accurate verb. */
  label?: string;
  /** Shown when the registry is empty/unreachable. Forward callers omit it (the
   *  picker stays hidden, the forward falls back to the server default PEOC); the
   *  route step passes a hint so the operator sees WHY no destination can be bound
   *  (route has no server default — §G honesty), instead of a mystifying disabled
   *  Advance. */
  emptyHint?: string;
}

export function DestinationPicker({ value, onChange, disabled, testid, label, emptyHint }: Props) {
  const [destinations, setDestinations] = useState<DeliveryDestination[]>([]);

  useEffect(() => {
    const ctrl = new AbortController();
    void fetchDeliveryDestinations(ctrl.signal)
      .then((list) => {
        setDestinations(list);
        // Auto-select the default (PEOC) so the one-click flow is preserved.
        const def = list.find((d) => d.is_default) ?? list[0];
        if (value === undefined && def) {
          onChange(def.id);
        }
      })
      .catch(() => {
        /* registry unreachable → leave undefined; forward falls back to default */
      });
    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (destinations.length === 0) {
    // Empty OR unreachable registry. Forward callers want this hidden (null) — their
    // server arm defaults to PEOC. The route step passes emptyHint so the operator
    // sees why no destination is bindable instead of a silently-disabled Advance.
    return emptyHint ? (
      <span
        className="text-[11px] text-amber-300/80"
        data-testid={testid ? `${testid}-empty` : undefined}
      >
        {emptyHint}
      </span>
    ) : null;
  }

  return (
    <label className="flex items-center gap-1.5 text-[11px] text-slate-400">
      <span className="uppercase tracking-wide">{label ?? 'Forward to'}</span>
      <select
        data-testid={testid ?? 'destination-picker'}
        value={value ?? ''}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value || undefined)}
        className="text-xs py-1 px-2 rounded bg-slate-800 border border-slate-600 text-slate-100 disabled:opacity-50"
      >
        {destinations.map((d) => (
          <option key={d.id} value={d.id}>
            {KIND_LABEL[d.kind]} — {d.name}
          </option>
        ))}
      </select>
    </label>
  );
}
